import { Eye, RotateCcw, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { LocalTime } from '~/components/common/LocalTime';
import PlacementVersionPreviewModal from '~/components/pages/ai/placements/PlacementVersionPreviewModal';
import { aiStatus } from '~/components/pages/ai/common/statusMaps';
import {
  ConfirmModal,
  DataTable,
  DropdownMenu,
  type DataTableColumn,
  StatusChip,
  LoadError,
} from '~/components/ui';
import { apiErrorMessage, type ApiErrorInput } from '~/lib/apiError';
import {
  useDeletePlacementVersionMutation,
  useGetPlacementHistoryQuery,
  useRestorePlacementMutation,
} from '~/modules/query/ai/placements';
import type { PlacementConfigDetail, PlacementVersionEntry } from '~/modules/query/ai/types';

/** How many versions the history asks for; service-ai gives no total or offset, so a full page may hide older ones. */
const HISTORY_LIMIT = 50;

interface PlacementHistoryTabProps {
  placementKey: string;
  placementName: string;
  /** The open preview, from `?version=`. */
  version: number | null;
  onOpenVersion: (versionNumber: number) => void;
  onCloseVersion: () => void;
  /** A restore returns the placement so the editor can reseed its form. */
  onRestored: (placement: PlacementConfigDetail) => void;
}

const changedBy = (entry: PlacementVersionEntry): string =>
  entry.changedBy.displayName?.trim() || entry.changedBy.id || 'Unknown';

/** Every saved, activated and restored version of a placement, with preview, restore and archive. */
export default function PlacementHistoryTab({
  placementKey,
  placementName,
  version,
  onOpenVersion,
  onCloseVersion,
  onRestored,
}: PlacementHistoryTabProps) {
  const { data, isLoading, isFetching, error, refetch } = useGetPlacementHistoryQuery({
    placementKey,
    limit: HISTORY_LIMIT,
  });
  const [restoreVersion, { isLoading: isRestoring }] = useRestorePlacementMutation();
  const [archiveVersion, { isLoading: isArchiving }] = useDeletePlacementVersionMutation();
  const [openMenuVersion, setOpenMenuVersion] = useState<number | null>(null);
  const [toRestore, setToRestore] = useState<PlacementVersionEntry | null>(null);
  const [toArchive, setToArchive] = useState<PlacementVersionEntry | null>(null);

  const entries = useMemo(() => data?.history ?? [], [data]);
  // Upstream's "Restored on": when a version was last restored, read from the restore entries' change notes.
  const restoredOnByVersion = useMemo(() => {
    const restored = new Map<number, string>();

    for (const entry of entries) {
      if (entry.action !== 'restored') {
        continue;
      }

      const match = entry.changeNote.match(/version\s+(\d+)/i) ?? entry.changeNote.match(/v(\d+)/i);
      const from = match ? Number(match[1]) : Number.NaN;

      if (Number.isFinite(from) && !restored.has(from)) {
        restored.set(from, entry.createdAt);
      }
    }

    return restored;
  }, [entries]);
  const previewEntry = useMemo(
    () =>
      version === null ? null : (entries.find((entry) => entry.versionNumber === version) ?? null),
    [entries, version],
  );

  // A version key that names nothing closes silently, but only once the history has loaded.
  useEffect(() => {
    if (version !== null && data && !isFetching && !previewEntry) {
      onCloseVersion();
    }
  }, [version, data, isFetching, previewEntry, onCloseVersion]);

  const columns: DataTableColumn<PlacementVersionEntry>[] = [
    {
      id: 'version',
      header: 'Version',
      cell: (entry) => (
        <span className="font-medium text-gray-800 dark:text-white/90">v{entry.versionNumber}</span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (entry) => <StatusChip {...aiStatus(entry.status)} />,
    },
    {
      id: 'date',
      header: 'Date',
      cell: (entry) => {
        const restoredOn = restoredOnByVersion.get(entry.versionNumber);

        return (
          <>
            <LocalTime value={entry.createdAt} />
            {restoredOn ? (
              <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                (Restored on <LocalTime value={restoredOn} />)
              </span>
            ) : null}
          </>
        );
      },
    },
    {
      id: 'changedBy',
      header: 'Changed by',
      cell: (entry) => changedBy(entry),
    },
    {
      id: 'changeNote',
      header: 'Change note',
      wrap: true,
      cell: (entry) =>
        entry.changeNote.trim() || <span className="text-gray-400 dark:text-gray-500">—</span>,
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (entry) => (
        <div className="flex w-full items-center justify-center">
          <DropdownMenu
            testId={`ai-placement-history-actions-${entry.versionNumber}`}
            ariaLabel={`Open actions for version ${entry.versionNumber}`}
            isOpen={openMenuVersion === entry.versionNumber}
            onOpenChange={(open) => setOpenMenuVersion(open ? entry.versionNumber : null)}
            items={[
              {
                label: 'Preview',
                testId: 'preview',
                icon: <Eye className="size-4" aria-hidden="true" />,
                onClick: () => onOpenVersion(entry.versionNumber),
              },
              {
                label: 'Restore',
                testId: 'restore',
                icon: <RotateCcw className="size-4" aria-hidden="true" />,
                onClick: () => setToRestore(entry),
              },
              {
                label: 'Archive',
                testId: 'archive',
                icon: <Trash2 className="size-4" aria-hidden="true" />,
                destructive: true,
                onClick: () => setToArchive(entry),
              },
            ]}
          />
        </div>
      ),
    },
  ];

  const confirmRestore = async () => {
    if (!toRestore || isRestoring) {
      return;
    }

    const versionNumber = toRestore.versionNumber;

    try {
      const response = await restoreVersion({
        placementKey,
        versionNumber,
        changeNote: `Restored from version ${versionNumber}`,
      }).unwrap();
      onRestored(response.placement);
      setToRestore(null);
      onCloseVersion();
      toast.success(`Version v${versionNumber} restored`);
    } catch (mutationError) {
      toast.error(
        apiErrorMessage(mutationError as ApiErrorInput) ??
          `Version v${versionNumber} could not be restored.`,
      );
    }
  };

  const confirmArchive = async () => {
    if (!toArchive || isArchiving) {
      return;
    }

    const versionNumber = toArchive.versionNumber;

    try {
      await archiveVersion({ placementKey, versionNumber }).unwrap();
      setToArchive(null);

      if (version === versionNumber) {
        onCloseVersion();
      }

      toast.success(`Version v${versionNumber} archived`);
    } catch (mutationError) {
      toast.error(
        apiErrorMessage(mutationError as ApiErrorInput) ??
          `Version v${versionNumber} could not be archived.`,
      );
    }
  };

  return (
    <>
      {error && !data ? (
        <LoadError
          what="the version history"
          message={apiErrorMessage(error)}
          onRetry={() => refetch()}
          testId="ai-placement-history"
        />
      ) : (
        <DataTable<PlacementVersionEntry>
          testId="ai-placement-history-table"
          loading={isLoading && !data}
          refreshing={isFetching && !!data}
          emptyMessage="No history yet."
          emptyDescription="Saving the placement, changing its status or restoring a version adds an entry here."
          data={entries}
          rowKey={(entry) => entry.id}
          columns={columns}
        />
      )}

      {entries.length >= HISTORY_LIMIT && (
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          Showing the latest {HISTORY_LIMIT} versions. Older ones are not listed.
        </p>
      )}

      {previewEntry ? (
        <PlacementVersionPreviewModal
          entry={previewEntry}
          placementName={placementName}
          onClose={onCloseVersion}
          onRestore={(entry) => {
            onCloseVersion();
            setToRestore(entry);
          }}
        />
      ) : null}

      <ConfirmModal
        isOpen={toRestore !== null}
        title="Restore version"
        message={`Restore ${placementName} to version v${toRestore?.versionNumber ?? ''}? The restore is saved as a new version, so the current one stays in history.`}
        confirmLabel="Restore version"
        testId="ai-placement-history-restore"
        onClose={() => setToRestore(null)}
        onConfirm={() => void confirmRestore()}
      />

      <ConfirmModal
        isOpen={toArchive !== null}
        title="Archive version"
        message={`Archive version v${toArchive?.versionNumber ?? ''} of ${placementName}?`}
        confirmLabel="Archive version"
        variant="danger"
        dangerNote="The version is removed from history and cannot be restored."
        testId="ai-placement-history-archive"
        onClose={() => setToArchive(null)}
        onConfirm={() => void confirmArchive()}
      />
    </>
  );
}
